import React, { ReactNode } from 'react';
import { useForm, FieldError } from 'react-hook-form';
import Error from '../Error';
import Button from '../Button';
import styles from './index.module.scss';

interface ConfirmationPromptProps {
  title: ReactNode;
  body?: ReactNode;
  error?: string;
  loading?: boolean;
  confirmationPhrase: string;
  confirmButtonText: string;
  onConfirm: () => void;
}

interface ConfirmationFormValues {
  confirmation: string;
}

function ConfirmationPrompt({
  title,
  body,
  error,
  loading,
  confirmationPhrase,
  confirmButtonText,
  onConfirm,
}: ConfirmationPromptProps) {
  const methods = useForm<ConfirmationFormValues>({
    mode: 'onChange',
    defaultValues: {
      confirmation: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = methods;

  const currentConfirmation = watch('confirmation');
  const fieldError = errors.confirmation as FieldError | undefined;

  return (
    <>
      <h1>{title}</h1>
      <form onSubmit={handleSubmit(onConfirm)}>
        {error && <Error data-testid="form-error">{error}</Error>}

        <div className={styles.formContents}>
          {body && <div>{body}</div>}
          <p>
            To continue, type <i>{confirmationPhrase}</i> in the text input
            below.
          </p>
          <div className={styles.inputContainer}>
            <label htmlFor="confirmation-input" className={styles.inputLabel}>
              {confirmationPhrase}
            </label>
            <input
              id="confirmation-input"
              type="text"
              className={`${styles.textInput} ${fieldError ? styles.inputError : ''}`}
              data-testid="confirmation-input"
              {...register('confirmation', {
                validate: (value) =>
                  value === confirmationPhrase ||
                  `Type "${confirmationPhrase}" to continue`,
              })}
            />
            {fieldError && (
              <Error>{fieldError.message}</Error>
            )}
          </div>
        </div>

        <Button
          type="submit"
          variant="normal"
          theme="dark"
          disabled={
            loading || !isValid || currentConfirmation !== confirmationPhrase
          }
          loading={loading ? 'true' : undefined}
          data-testid="confirmation-button"
        >
          {confirmButtonText}
        </Button>
      </form>
    </>
  );
}

export default ConfirmationPrompt;
